// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import PageTitle from "../../../components/PageTitle";
import PageBody from "../../../components/PageBody";
import { navigate, RouteComponentProps } from "@reach/router";
import {
  AuthentifiedUser,
  UserInfoResponseDTO,
  UserSearchRequestDTO
} from "../../../../api/backend-openapi";
import { useStore } from "effector-react";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";
import { NboAction } from "../../../types/AuthTypes";
import { SpinIcon } from "../../../components/SpinIcon";
import { columns } from "./UsersListColumns";
import { Button, Divider, message, Table } from "antd";
import { SorterResult, TablePaginationConfig } from "antd/lib/table/interface";
import { PlusCircleOutlined } from "@ant-design/icons";
import {
  // tabTemp,
  UserManagementStore
} from "../../../../state/userManagement/UserManagementStore";
import {
  requestDeleteUser,
  requestGetUsersList
} from "../../../../state/userManagement/UserManagementEffects";
import {
  deleteUser,
  setUsers
} from "../../../../state/userManagement/UserManagementEvents";
import UsersListSearchForm from "./UsersListSearchForm";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { orderAscend } from "../../../../utils/Constant";
import {
  preferedRowsPerPage,
  preferedRowsPerPageChanged
} from "../../../../state/localPreferences/localPreferences";

const UsersListScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const [editMode] = useState<boolean>(false);
  const [searchDto, setSearchDto] = useState<UserSearchRequestDTO | {}>({});

  const branchList = useStore(BranchesStore);

  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  const userManagmentStore: UserInfoResponseDTO[] | null = useStore(
    UserManagementStore
  );

  const canDelete: boolean =
    (authentifiedUser &&
      authentifiedUser.rights &&
      authentifiedUser.rights.nboUserManagement &&
      typeof authentifiedUser.rights.nboUserManagement.find(
        user => user === NboAction.nboDelete
      ) === "string") ||
    false;
  const canCreate: boolean =
    (authentifiedUser &&
      authentifiedUser.rights &&
      authentifiedUser.rights.nboUserManagement &&
      typeof authentifiedUser.rights.nboUserManagement.find(
        user => user === NboAction.nboCreate
      ) === "string") ||
    false;

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(true);

  const rowsPerPage = useStore(preferedRowsPerPage);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: rowsPerPage,
    showSizeChanger: true
  });

  const handleTableChange = async (
    newPagination: any,
    filters: any,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const restSorter = Array.isArray(sorter)
      ? sorter.length > 0
        ? sorter[0]
        : undefined
      : sorter;
    const searchCriteria = {
      pagination: newPagination,
      sorter: restSorter,
      dto: searchDto
    };
    await requestGetUsersList(searchCriteria);
    setPagination(newPagination);
    preferedRowsPerPageChanged(newPagination.pageSize);
  };

  useEffect(() => {
    return requestGetUsersList.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setUsers(result.data.listUsersDTO);
        setDataHasLoaded(true);
        setPagination({
          ...pagination,
          total: result.data.count
        });
        preferedRowsPerPageChanged(pagination.pageSize);
      } else {
        message.error(t("users.load_failed"));
      }
    });
  });

  useEffect(() => {
    return requestDeleteUser.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteUser(params.id);
        message.success(t("users.global_buttons.delete.success"));
      } else {
        message.error(t("users.global_buttons.delete.error"));
      }
    });
  });

  // Load user on page load
  useEffect(() => {
    requestGetUsersList({
      pagination,
      sorter: {
        columnKey: "sub",
        order: orderAscend
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageHeader>
        <PageTitle title={t(editMode ? "users.edit.title" : "users.title")} />
      </PageHeader>
      <PageBody>
        <UsersListSearchForm
          onSearchDtoUpdate={setSearchDto}
          pagination={pagination}
          dataHasLoaded={dataHasLoaded}
          setDataHasLoaded={setDataHasLoaded}
        />
        <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
          <div className="flex-grow-1 d-flex align-items-center">
            <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
              <h3 className="text-secondary mb-0">{t("users.list_title")}</h3>
            </Divider>
          </div>
          {canCreate && (
            <div className="d-flex align-items-center divider-sm-transparent">
              <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
                <Button
                  type="default"
                  className="btn-secondary"
                  onClick={() => {
                    navigate("/users/add");
                  }}
                >
                  <PlusCircleOutlined /> {t("users.global_buttons.add")}
                </Button>
              </Divider>
            </div>
          )}
        </div>
        <Table
          className="nbo-user-list"
          style={{ whiteSpace: "break-spaces" }}
          locale={{ emptyText: dataHasLoaded ? "" : " " }}
          loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
          columns={columns(canDelete, branchList)}
          dataSource={userManagmentStore}
          onChange={handleTableChange}
          pagination={pagination}
          showSorterTooltip={false}
          rowKey={record => record.id}
          rowClassName="cursor-pointer"
          onRow={record => {
            return {
              onClick: () => navigate(`/users/${record.id}`)
            };
          }}
        />
      </PageBody>
    </>
  );
};

export default UsersListScreen;
